/**
 * DostCoPilotPanel (Enhancement: Dost Co-pilot).
 *
 * A collapsible right-side panel on the admin event editor's Content tab
 * that shows 3-6 real-time critiques from Dost. Debounced 3s after edits.
 *
 * Props:
 *   eventId   - string, required
 *   blocks    - array of current content blocks (from parent)
 *   onJumpTo  - (block_type) => void, called when admin clicks "Fix" on a critique
 *
 * The panel auto-refreshes when `blocks` changes (debounced). Admin can also
 * click the "Refresh" button in the header to force-run.
 *
 * Collapsed by default on <1200px screens; expanded on ≥1200px.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles, RefreshCcw, X, ChevronRight, ChevronLeft,
  AlertTriangle, Info as InfoIcon, Wand2,
} from 'lucide-react';
import api from '@/utils/api';

// Match the block-type -> friendly label mapping used by the editor.
const BLOCK_LABEL = {
  overview: 'Overview',
  what_to_expect: 'What to Expect',
  date_time: 'Date & Time',
  venue: 'Venue',
  schedule: 'Schedule',
  food: 'Food',
  gallery: 'Gallery',
  dress_code: 'Dress Code',
  faq: 'FAQ',
  policies: 'Policies',
  hosts: 'Hosts',
  sponsors: 'Sponsors',
  custom_rich_text: 'Custom text',
  hero: 'Hero',
  rsvp_cta: 'RSVP button',
  video: 'Video',
  overall: 'Overall',
};

const DEBOUNCE_MS = 3000;

const DostCoPilotPanel = ({ eventId, blocks, onApplyCritique }) => {
  const [collapsed, setCollapsed] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1200 : false
  );
  const [loading, setLoading] = useState(false);
  const [critiques, setCritiques] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [err, setErr] = useState('');
  const [quotaOut, setQuotaOut] = useState(false);
  const [meta, setMeta] = useState({ latency_ms: 0, tokens_used: 0, at: null });
  const debounceRef = useRef(null);
  const firstRunRef = useRef(true);

  // Compact block content payload so the endpoint can ground its critique.
  const currentContent = useMemo(() => {
    const map = {};
    (blocks || []).forEach((b) => {
      const c = b?.content || {};
      let txt = c.body_html || c.body || c.dress_code || '';
      if (Array.isArray(c.items) && c.items.length) {
        txt += '\n' + c.items.slice(0, 6).map((it) => (
          `Q: ${it?.question || ''} A: ${it?.answer || ''}`
        )).join('\n');
      }
      if (txt) map[b.block_type] = String(txt).slice(0, 1200);
    });
    return map;
  }, [blocks]);

  const fetchCritiques = useCallback(async () => {
    if (!eventId) return;
    setLoading(true); setErr(''); setQuotaOut(false);
    try {
      const { data } = await api.post('/dost/admin/critique', {
        event_id: eventId,
        current_content: currentContent,
      });
      setCritiques(Array.isArray(data.critiques) ? data.critiques : []);
      setMeta({ latency_ms: data.latency_ms || 0, tokens_used: data.tokens_used || 0, at: Date.now() });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setQuotaOut(true);
        setCritiques([]);
      } else {
        setErr(e?.response?.data?.detail || 'Dost had trouble critiquing. Try again shortly.');
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, currentContent]);

  // Debounced auto-refresh when blocks change.
  useEffect(() => {
    if (collapsed) return;
    // Skip the first run's debounce - fire immediately on mount instead.
    if (firstRunRef.current) {
      firstRunRef.current = false;
      fetchCritiques();
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCritiques, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [currentContent, collapsed, fetchCritiques]);

  const dismiss = (i) => {
    const s = new Set(dismissed); s.add(i); setDismissed(s);
  };

  const visible = critiques.filter((_, i) => !dismissed.has(i));

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed right-4 top-32 z-30 flex items-center gap-2 px-3 py-2 rounded-full bg-secondary-500 text-white shadow-gold hover:bg-secondary-600 transition-colors"
        data-testid="copilot-expand-btn"
        title="Show Dost Co-pilot"
      >
        <Sparkles className="h-4 w-4" />
        <span className="text-xs font-semibold">Dost Co-pilot</span>
        {visible.length > 0 && (
          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-white text-secondary-800 text-[10px] font-bold px-1">
            {visible.length}
          </span>
        )}
        <ChevronLeft className="h-3.5 w-3.5 opacity-80" />
      </button>
    );
  }

  return (
    <aside
      className="rounded-xl border border-secondary-200 bg-white shadow-soft overflow-hidden"
      data-testid="copilot-panel"
    >
      <header className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-secondary-50 to-white border-b border-secondary-100">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center">
            <Sparkles className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-primary-900">Dost Co-pilot</div>
            <div className="text-[10px] uppercase tracking-widest text-secondary-700">Live critique</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="sm"
            onClick={fetchCritiques}
            disabled={loading}
            className="h-8 w-8 p-0"
            title="Refresh critiques"
            data-testid="copilot-refresh-btn"
          >
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => setCollapsed(true)}
            className="h-8 w-8 p-0"
            title="Collapse panel"
            data-testid="copilot-collapse-btn"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </header>

      <div className="p-3 max-h-[70vh] overflow-y-auto">
        {loading && critiques.length === 0 && (
          <div className="flex items-center gap-2 text-xs text-secondary-700 px-2 py-3">
            <Sparkles className="h-3.5 w-3.5 animate-pulse" />
            Dost is reading your event…
          </div>
        )}

        {quotaOut && (
          <div className="p-3 rounded-md bg-warning-50 text-warning-800 text-xs" data-testid="copilot-quota">
            Critiques rest until tomorrow. You&apos;ve used today&apos;s Co-pilot budget.
          </div>
        )}

        {err && !quotaOut && (
          <div className="p-3 rounded-md bg-error-50 text-error-800 text-xs" data-testid="copilot-error">
            {err}
          </div>
        )}

        {!loading && !err && !quotaOut && visible.length === 0 && critiques.length > 0 && (
          <div className="text-xs text-neutral-500 italic px-2 py-4 text-center">
            All critiques dismissed. Refresh to get a fresh pass.
          </div>
        )}

        {!loading && !err && !quotaOut && critiques.length === 0 && (
          <div className="text-xs text-neutral-500 italic px-2 py-4 text-center">
            Nothing to critique yet. Start writing and Dost will chime in.
          </div>
        )}

        <ul className="space-y-2">
          {critiques.map((c, i) => {
            if (dismissed.has(i)) return null;
            const isWarn = c.severity === 'warning';
            const label = BLOCK_LABEL[c.block_type] || c.block_type;
            return (
              <li
                key={i}
                className={`p-3 rounded-lg border ${isWarn ? 'border-error-200 bg-error-50/30' : 'border-secondary-200 bg-secondary-50/20'}`}
                data-testid={`copilot-critique-${c.block_type}`}
              >
                <div className="flex items-start gap-2">
                  <div className={`shrink-0 mt-0.5 ${isWarn ? 'text-error-600' : 'text-secondary-700'}`}>
                    {isWarn ? <AlertTriangle className="h-3.5 w-3.5" /> : <InfoIcon className="h-3.5 w-3.5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Badge
                        variant="outline"
                        className={`text-[9px] uppercase tracking-widest px-1.5 py-0 ${isWarn ? 'border-error-300 text-error-800' : 'border-secondary-300 text-secondary-800'}`}
                      >
                        {label}
                      </Badge>
                    </div>
                    <p className="text-xs text-neutral-800 leading-snug">{c.message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {onApplyCritique && c.block_type !== 'overall' && BLOCK_LABEL[c.block_type] && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[11px] px-2 border-secondary-300 text-secondary-800 hover:bg-secondary-50"
                          onClick={() => onApplyCritique(c)}
                          data-testid={`copilot-fix-${c.block_type}`}
                        >
                          <Wand2 className="h-3 w-3 mr-1" /> Fix with Dost
                        </Button>
                      )}
                      <button
                        onClick={() => dismiss(i)}
                        className="text-[11px] text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1"
                        data-testid={`copilot-dismiss-${i}`}
                      >
                        <X className="h-3 w-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {meta.at && (
          <div className="text-[10px] text-neutral-400 mt-3 px-1">
            Last run {new Date(meta.at).toLocaleTimeString()} · {meta.latency_ms}ms · {meta.tokens_used} tokens
          </div>
        )}
      </div>
    </aside>
  );
};

export default DostCoPilotPanel;
